// The secondary "add manual account" path (execution plan §s03 / WS-3 step
// 6): posts POST /api/accounts and returns to /accounts. No file drop zone,
// no institution picker -- that is Plaid Link's job and out of scope here.
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@moneyskyline/ui';
import { HonestInline } from '@/components/HonestInline';
import { accountKindSchema, type AccountKind } from '@/api/hooks/useAccounts';
import { useCreateManualAccount } from './useCreateManualAccount';

const KIND_OPTIONS = accountKindSchema.options;
const CURRENCY_OPTIONS = ['USD', 'CAD', 'EUR', 'GBP', 'JPY'] as const;
const BUSINESS_USE_OPTIONS = ['none', 'mixed', 'primary'] as const;

export function ManualAccountForm({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [kind, setKind] = useState<AccountKind>('checking');
  const [currency, setCurrency] = useState('USD');
  const [businessUse, setBusinessUse] = useState<(typeof BUSINESS_USE_OPTIONS)[number]>('none');
  const [openingBalance, setOpeningBalance] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const mutation = useCreateManualAccount();

  useEffect(() => {
    if (!open) return undefined;

    let raf2 = 0;
    let raf3 = 0;
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        raf3 = window.requestAnimationFrame(() => {
          if (!dialogRef.current?.contains(document.activeElement)) nameInputRef.current?.focus();
        });
      });
    });

    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      window.cancelAnimationFrame(raf3);
    };
  }, [open]);

  useEffect(() => {
    if (mutation.isError) nameInputRef.current?.focus();
  }, [mutation.isError]);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const balance = openingBalance.trim();
    mutation.mutate(
      {
        display_name: displayName,
        kind,
        currency,
        business_use: businessUse,
        ...(balance ? { opening_balance: balance } : {}),
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          navigate('/accounts');
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        ref={dialogRef}
        data-testid="manual-account-form"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          nameInputRef.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>Add a manual account</DialogTitle>
          <DialogDescription>Track a balance MoneySkyline does not sync automatically.</DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
          <label className="flex flex-col gap-1 text-sm">
            Name
            <input
              ref={nameInputRef}
              className="min-h-11 rounded-md border border-input px-3"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              required
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Type
            <select
              className="min-h-11 rounded-md border border-input px-3"
              value={kind}
              onChange={(event) => setKind(event.target.value as AccountKind)}
            >
              {KIND_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Currency
            <select
              className="min-h-11 rounded-md border border-input px-3"
              value={currency}
              onChange={(event) => setCurrency(event.target.value)}
            >
              {CURRENCY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Business use
            <select
              className="min-h-11 rounded-md border border-input px-3"
              value={businessUse}
              onChange={(event) => setBusinessUse(event.target.value as (typeof BUSINESS_USE_OPTIONS)[number])}
            >
              {BUSINESS_USE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Current balance (optional)
            <input
              className="min-h-11 rounded-md border border-input px-3"
              inputMode="decimal"
              value={openingBalance}
              onChange={(event) => setOpeningBalance(event.target.value)}
              placeholder="Leave blank if you do not know it"
            />
          </label>
          <p role="status" className="text-sm text-muted-foreground">
            A dated opening balance and change history aren’t available yet.
          </p>
          {mutation.isError && <HonestInline kind="api-unavailable" />}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Adding…' : 'Add account'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ManualAccountForm;
